import { useDeferredValue, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tags } from 'lucide-react';
import { PERMISSIONS, type ExpenseCategorySummary } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/ui/PageHeader';
import { Pagination } from '@/components/ui/Pagination';
import { Select } from '@/components/ui/Select';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode, apiErrorMessage } from '@/services/apiClient';
import {
  useCreateExpenseCategoryMutation,
  useExpenseCategoriesQuery,
  useExpenseCategoryActivationMutation,
  useUpdateExpenseCategoryMutation,
} from '../api/expenses.api';
import { InactiveBadge } from '../components/ExpenseBadges';
import { cameFromRecordExpense } from './categoriesReturn';

const PAGE_SIZE = 20;

/**
 * How the business classifies what it spends.
 *
 * Tenant-wide master data, behind `expenses.categories.manage` — a branch manager records what
 * their branch spends without deciding how the whole company describes itself. Reading is a
 * different matter and needs only `expenses.view`, which is why the picker on the record page works
 * for everyone.
 *
 * Nothing is deleted. A category goes out of use, which stops new expenses choosing it and leaves
 * every expense already recorded against it exactly as it was.
 */
export function ExpenseCategoriesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = usePermissions();
  // Opened from the record page by someone who may record: offer the way straight back.
  const [backToRecord] = useState(
    () => cameFromRecordExpense(location.state) && can(PERMISSIONS.EXPENSES_MANAGE),
  );

  const [search, setSearch] = useState('');
  const [active, setActive] = useState<'all' | 'true' | 'false'>('all');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<ExpenseCategorySummary | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [feedback, setFeedback] = useState<string | null>(null);

  const deferredSearch = useDeferredValue(search);
  const { data, isPending, isError, error, refetch, isFetching } = useExpenseCategoriesQuery({
    page,
    pageSize: PAGE_SIZE,
    search: deferredSearch,
    ...(active === 'all' ? {} : { active: active === 'true' }),
  });

  const create = useCreateExpenseCategoryMutation();
  const update = useUpdateExpenseCategoryMutation();
  const setActivation = useExpenseCategoryActivationMutation();

  const saving = create.isPending || update.isPending;
  const saveError = create.error ?? update.error;
  const stale = apiErrorCode(saveError) === 'EXPENSE_CATEGORY_VERSION_CONFLICT';

  const openCreate = () => {
    setEditing(null);
    setName('');
    setNameAr('');
    setIsCreating(true);
  };

  const openEdit = (category: ExpenseCategorySummary) => {
    setIsCreating(false);
    setEditing(category);
    setName(category.name);
    setNameAr(category.nameAr ?? '');
  };

  const closeDialog = () => {
    setIsCreating(false);
    setEditing(null);
    create.reset();
    update.reset();
  };

  const save = () => {
    if (!name.trim()) return;
    const body = { name: name.trim(), ...(nameAr.trim() ? { nameAr: nameAr.trim() } : {}) };

    if (editing) {
      update.mutate(
        { id: editing.id, version: editing.version, ...body },
        {
          onSuccess: (saved) => {
            setFeedback(t('expenses.categories.saved', { name: saved.name }));
            closeDialog();
          },
          // Somebody else changed it. Refresh what the server now says and keep what was typed,
          // so the person can see both and decide rather than have their edit thrown away.
          onError: () => void refetch(),
        },
      );
      return;
    }

    create.mutate(body, {
      onSuccess: (saved) => {
        setFeedback(t('expenses.categories.added', { name: saved.name }));
        closeDialog();
      },
    });
  };

  const toggle = (category: ExpenseCategorySummary) =>
    setActivation.mutate(
      { id: category.id, version: category.version, isActive: !category.isActive },
      {
        onSuccess: (saved) =>
          setFeedback(
            saved.isActive
              ? t('expenses.categories.reactivated', { name: saved.name })
              : t('expenses.categories.deactivated', { name: saved.name }),
          ),
        onError: () => void refetch(),
      },
    );

  const columns: Column<ExpenseCategorySummary>[] = [
    {
      key: 'name',
      header: t('expenses.categories.fields.name'),
      primary: true,
      render: (category) => (
        <span className="flex flex-col">
          <span className="text-ink font-medium" dir="auto">
            {category.name}
          </span>
          <span className="text-muted text-xs" dir="auto">
            {category.nameAr ?? '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('expenses.fields.status'),
      render: (category) =>
        category.isActive ? (
          <span className="text-muted text-sm">{t('expenses.categories.inUse')}</span>
        ) : (
          <InactiveBadge />
        ),
    },
    {
      key: 'actions',
      header: t('common.actions'),
      align: 'end',
      render: (category) => (
        <span className="flex flex-wrap items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('expenses.categories.editNamed', { name: category.name })}
            onClick={() => openEdit(category)}
          >
            {t('common.edit')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            aria-label={
              category.isActive
                ? t('expenses.categories.deactivateNamed', { name: category.name })
                : t('expenses.categories.reactivateNamed', { name: category.name })
            }
            onClick={() => toggle(category)}
            disabled={setActivation.isPending}
          >
            {category.isActive
              ? t('expenses.categories.deactivate')
              : t('expenses.categories.reactivate')}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('expenses.categories.title')}
        subtitle={t('expenses.categories.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {backToRecord ? (
              <Button variant="secondary" onClick={() => void navigate('/finance/expenses/new')}>
                {t('expenses.noCategories.backToRecord')}
              </Button>
            ) : (
              <Button variant="secondary" onClick={() => void navigate('/finance/expenses')}>
                {t('expenses.backToList')}
              </Button>
            )}
            <Button onClick={openCreate}>{t('expenses.categories.add')}</Button>
          </div>
        }
      />

      {feedback ? <Alert tone="success">{feedback}</Alert> : null}
      {setActivation.isError ? (
        <Alert tone="error">
          {apiErrorMessage(setActivation.error) ?? t('expenses.categories.saveError')}
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('expenses.categories.title')}
          subtitle={
            data ? t('expenses.categories.count', { count: data.pagination.totalItems }) : undefined
          }
        />

        <div className="border-line grid gap-3 border-b px-4 py-3 sm:grid-cols-2">
          <Input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder={t('expenses.categories.searchPlaceholder')}
            aria-label={t('expenses.categories.searchLabel')}
          />
          <Select
            options={[
              { value: 'all', label: t('expenses.categories.filters.all') },
              { value: 'true', label: t('expenses.categories.filters.inUse') },
              { value: 'false', label: t('expenses.categories.filters.outOfUse') },
            ]}
            value={active}
            aria-label={t('expenses.fields.status')}
            onChange={(event) => {
              setActive(event.target.value as 'all' | 'true' | 'false');
              setPage(1);
            }}
          />
        </div>

        <DataTable
          caption={t('expenses.categories.tableCaption')}
          columns={columns}
          rows={data?.items}
          rowKey={(category) => category.id}
          isLoading={isPending}
          isError={isError}
          errorMessage={apiErrorMessage(error) ?? t('expenses.categories.loadError')}
          onRetry={() => void refetch()}
          emptyIcon={Tags}
          emptyTitle={t('expenses.categories.empty')}
          emptyDescription={t('expenses.categories.emptyHint')}
        />

        {data ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} isBusy={isFetching} />
        ) : null}
      </Card>

      <Modal
        isOpen={isCreating || editing !== null}
        onClose={closeDialog}
        title={editing ? t('expenses.categories.editTitle') : t('expenses.categories.addTitle')}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="secondary" onClick={closeDialog} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button onClick={save} isLoading={saving} disabled={!name.trim() || saving}>
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          {saveError ? (
            <Alert tone="error">
              {stale
                ? t('expenses.categories.staleVersion')
                : (apiErrorMessage(saveError) ?? t('expenses.categories.saveError'))}
            </Alert>
          ) : null}
          {stale ? <Alert tone="info">{t('expenses.categories.staleKept')}</Alert> : null}

          <Input
            label={t('expenses.categories.fields.name')}
            required
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            label={t('expenses.categories.fields.nameAr')}
            dir="rtl"
            value={nameAr}
            hint={t('expenses.categories.fields.nameArHint')}
            onChange={(event) => setNameAr(event.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
}
